import React, { useEffect, useState, useContext } from 'react';
import { api } from '../api';
import { AuthContext } from './AuthGate';

const STATUS_STYLES = {
  open:        'bg-slate-700/50 text-slate-300',
  in_progress: 'bg-indigo-600/30 text-indigo-200',
  closed:      'bg-emerald-600/30 text-emerald-200 line-through opacity-70',
};

const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', closed: 'Closed' };

const FILTERS = [
  { id: 'mine',    label: 'My open tasks',  params: { mine: true, status: 'open' } },
  { id: 'mine_active', label: 'My active', params: { mine: true } },
  { id: 'overdue', label: 'Overdue',        params: { overdue_only: true } },
  { id: 'team',    label: 'Team open',      params: { status: 'open' } },
  { id: 'all',     label: 'All',            params: {} },
];

export default function TasksPanel() {
  const auth = useContext(AuthContext);
  const [filterId, setFilterId] = useState('mine');
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ mine_open: 0, mine_overdue: 0, team_open: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); api.listUsers().then(setUsers).catch(() => {}); /* eslint-disable-next-line */ }, [filterId]);

  async function load() {
    setLoading(true);
    try {
      const filter = FILTERS.find((f) => f.id === filterId);
      const [list, c] = await Promise.all([
        fetchTasks(filter?.params || {}),
        api.fetch('/api/tasks/counts').catch(() => null),
      ]);
      setTasks(list || []);
      if (c) setCounts(c);
    } finally { setLoading(false); }
  }

  async function fetchTasks(params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) qs.set(k, String(v));
    return api.fetch(`/api/tasks${qs.toString() ? '?' + qs.toString() : ''}`);
  }

  async function toggleStatus(t) {
    const next = t.status === 'closed' ? 'open' : t.status === 'open' ? 'in_progress' : 'closed';
    try {
      setError('');
      await api.fetch(`/api/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      await load();
    } catch (err) {
      setError('Failed to update task: ' + (err.message || err));
      await load();
    }
  }

  async function reassign(t, assignee_id) {
    try {
      setError('');
      await api.fetch(`/api/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ assignee_id: assignee_id || null }) });
      await load();
    } catch (err) {
      setError('Failed to reassign task: ' + (err.message || err));
      await load();
    }
  }

  async function remove(t) {
    if (!confirm(`Delete "${t.name}"?`)) return;
    try {
      setError('');
      await api.fetch(`/api/tasks/${t.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError('Failed to delete task: ' + (err.message || err));
      await load();
    }
  }

  return (
    // App shell wraps panels in overflow-hidden — each panel owns its own
    // scroll. Without h-full + overflow-y-auto the task list gets cut off
    // below the fold.
    <div className="h-full overflow-y-auto scrollable-content">
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-white">Tasks</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium text-white"
        >+ New task</button>
      </div>
      <div className="text-slate-500 text-xs mb-6">
        {counts.mine_open} open assigned to you · {counts.mine_overdue} overdue · {counts.team_open} open across the team
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/40 rounded text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilterId(f.id)}
            className={`px-3 py-1.5 text-xs rounded-full border ${
              filterId === f.id
                ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
            }`}
          >{f.label}</button>
        ))}
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {!loading && tasks.length === 0 && (
        <div className="text-slate-500 text-sm italic py-8 text-center">No tasks in this filter.</div>
      )}

      {/* Task list */}
      <div className="space-y-1">
        {tasks.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-800/50 group ${t.overdue ? 'border-l-2 border-rose-500' : ''}`}
          >
            <button
              onClick={() => toggleStatus(t)}
              className={`w-5 h-5 rounded border flex items-center justify-center text-xs flex-shrink-0
                ${t.status === 'closed'
                  ? 'bg-emerald-600 border-emerald-500 text-white'
                  : t.status === 'in_progress'
                    ? 'bg-indigo-600/40 border-indigo-500'
                    : 'border-slate-600 hover:border-slate-400'}`}
              title="Toggle status"
            >
              {t.status === 'closed' ? '✓' : t.status === 'in_progress' ? '…' : ''}
            </button>

            <div className="flex-1 min-w-0">
              <div className={`text-sm ${t.status === 'closed' ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                {t.name}
              </div>
              {(t.deal_name || t.notes) && (
                <div className="text-xs text-slate-500 truncate">
                  {t.deal_name && <span className="mr-2 text-slate-400">[{t.deal_name}]</span>}
                  {t.notes}
                </div>
              )}
            </div>

            <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLES[t.status] || ''}`}>
              {STATUS_LABEL[t.status] || t.status}
            </span>

            <select
              value={t.assignee_id || ''}
              onChange={(e) => reassign(t, e.target.value)}
              className="bg-transparent text-xs text-slate-400 border border-slate-700 rounded px-2 py-1 max-w-[140px]"
              title="Assignee"
            >
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>

            <span className={`text-xs tabular-nums ${t.overdue ? 'text-rose-400' : 'text-slate-500'} min-w-[80px] text-right`}>
              {t.due_date || ''}
            </span>

            <button
              onClick={() => remove(t)}
              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 text-sm px-1"
              title="Delete"
            >×</button>
          </div>
        ))}
      </div>

      {showCreate && (
        <CreateTaskModal
          users={users}
          defaultAssigneeId={auth?.user?.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
    </div>
  );
}


export function CreateTaskModal({ users, defaultAssigneeId, defaultDealId, defaultDealName, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId || '');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e?.preventDefault?.();
    if (!name.trim()) { setError('Name is required'); return; }
    setBusy(true); setError('');
    try {
      const body = { name: name.trim() };
      if (notes.trim()) body.notes = notes.trim();
      if (assigneeId)   body.assignee_id = assigneeId;
      if (dueDate)      body.due_date = dueDate;
      if (defaultDealId) body.deal_id = defaultDealId;
      await api.fetch('/api/tasks', { method: 'POST', body: JSON.stringify(body) });
      onCreated?.();
    } catch (e) {
      setError(e.message || 'Failed to create');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit}
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-white mb-1">New task</h2>
        {defaultDealName && (
          <div className="text-[11px] text-indigo-300 mb-3">
            Attached to deal: <span className="font-medium">{defaultDealName}</span>
          </div>
        )}
        {!defaultDealName && <div className="mb-3" />}
        <label className="block mb-3">
          <span className="text-xs text-slate-400">Name</span>
          <input
            autoFocus value={name} onChange={(e) => setName(e.target.value)}
            className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs text-slate-400">Assignee</span>
            <select
              value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            />
          </label>
        </div>
        <label className="block mb-4">
          <span className="text-xs text-slate-400">Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
          />
        </label>
        {error && <div className="text-red-400 text-xs mb-3">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={busy}
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded text-sm font-medium text-white"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
